<script setup lang="ts">
import { onMounted, computed, watch, type Ref } from "vue";
import ChatAI from "@/components/chatAI/chatAI.vue";
import { useSearchPrams } from "@/stores/searchParams";
import { useAccountStore } from "@/stores/account";
import { useUserStore } from "@/stores/user";
import Tenant from "@/function/tenant";
import type { CustomerJsonInfo } from "@zhihaoscm/entity";
import { useRuntimeConfig } from "@/stores/runtimeConfig";
import sensors from "sa-sdk-javascript";
import { TRACK_TYPE } from "@zhihaoscm/meta";

type MenusItem = {
    name: string;
    path: string;
    trackEvent?: () => void;
};

const useSearchParam = useSearchPrams();
const isChainCloud = Tenant.getInstance().isChainCloud;
const customerId = ref(Tenant.getInstance().enterpriseCustomVo?.customer?.id);
const accountStore = useAccountStore();
const userStore = useUserStore();
const runtimeConfig = useRuntimeConfig();
const router = useRouter();
const route = useRoute();
const isCollapse = ref(false);

// 控制台相关页面路径
const consoleAboutPath = [
    "/console/",
    "/logistics/",
    "/messageRelease/",
    "/orderCenter/",
    "/followCenter/",
    "/registration/"
];

const orderMenus: MenusItem[] = reactive([
    { name: "我的订单", path: "/orderCenter/myOrder" },
    { name: "我的发票", path: "/orderCenter/myInvoice" }
]);

const logisticsMenus: MenusItem[] = reactive([
    {
        name: "查船",
        path: "/logistics/findLocation",
        trackEvent: () => {
            sensors.track("171", {
                type: TRACK_TYPE.ELEMENT_CLICK.code
            });
        }
    },
    {
        name: "查车",
        path: "/logistics/truckLocation",
        trackEvent: () => {
            sensors.track("173", {
                type: TRACK_TYPE.ELEMENT_CLICK.code
            });
        }
    },
    { name: "运单", path: "/logistics/shipOrder" },
    { name: "船务信息服务费", path: "/logistics/shipInfoServiceFee" }
]);

const enterpriseAnnouncementsMenus: MenusItem[] = reactive([
    { name: "公司公告", path: "/messageRelease/enterpriseAnnouncements" },
    { name: "公司资讯", path: "/messageRelease/enterpriseInformation" }
]);

const followMenus: MenusItem[] = reactive([
    { name: "我的足迹", path: "/followCenter/footprints" },
    { name: "我的收藏", path: "/followCenter/favorite" },
    { name: "我的关注", path: "/followCenter/interest" },
    { name: "我的预警", path: "/followCenter/priceWarning" }
]);

const changeCollapse = () => {
    isCollapse.value = !isCollapse.value;
};

// 过滤掉三级路由
const extractPath = (path: string): string => {
    const match = path.match(/^\/[^/]+\/[^/]+/);
    return match ? match[0] : "";
};

const defaultActive: Ref<string> = ref("");
const defaultOpeneds: Ref<string[]> = ref(["console"]);
watch(
    () => router.currentRoute.value.path,
    () => {
        runtimeConfig.connectWebsocket();
        defaultActive.value = extractPath(router.currentRoute.value.path);
        defaultOpeneds.value = [router.currentRoute.value.fullPath.split("/")[1]];
    },
    { immediate: true }
);

onMounted(() => {
    defaultActive.value = extractPath(router.currentRoute.value.path);
});
const isShow = (paths: string[]) => {
    return paths.some(path => route.path.includes(path));
};

/** 是否显示企业菜单 */
const showEnterpriseAnnouncementsMenu = () => {
    /** 企业成员列表 */
    const userList =
        Tenant.getInstance().enterpriseCustomVo?.enterpriseCustom.customJsons?.map(
            (item: CustomerJsonInfo) => item.id
        ) || [];

    return (
        isShow(consoleAboutPath) &&
        !isChainCloud &&
        (customerId.value === userStore.customerId || userList.includes(userStore.customerId))
    );
};

const switchTab = (item: MenusItem) => {
    item.trackEvent?.();

    useSearchParam.clearParams();
};

const syncCustomerId = async () => {
    // 解决返回页面后在未拿到_enterpriseCustomVo前 先执行了customerId 即undefind;
    // 如没有customerId， 则重新执行一次init，确保customerId是最新值
    if (!customerId.value) {
        await Tenant.getInstance().init();
        customerId.value = Tenant.getInstance().enterpriseCustomVo?.customer?.id;
    }
};
syncCustomerId();
const showChatAI = computed(() => {
    return isChainCloud || (!isChainCloud && customerId.value == userStore.customerId);
});
</script>

<template>
    <div v-if="!isCollapse" class="menu">
        <!-- <ClientOnly fallback-tag="span"> -->
        <ChatAI v-if="showChatAI" />
        <!-- </ClientOnly> -->
        <el-menu
            :default-openeds="defaultOpeneds"
            :default-active="defaultActive"
            class="el-menu-vertical"
            :collapse="isCollapse"
            router
        >
            <el-sub-menu v-if="isShow(['/me/'])" index="me">
                <template #title>
                    <el-icon>
                        <i class="iconfont icon-files" />
                    </el-icon>
                    <span>账号管理</span>
                </template>
                <el-menu-item index="/me/info"> 个人信息 </el-menu-item>
                <el-menu-item index="/me/safeSetting"> 安全设置 </el-menu-item>
                <el-menu-item index="/me/authentication"> 认证 </el-menu-item>
                <el-menu-item v-if="accountStore.isSubAccount" index="/me/subAccountManagement/detail">
                    子账号信息
                </el-menu-item>
                <el-menu-item v-if="accountStore.isRootAccount" index="/me/subAccountManagement">
                    子账号管理
                </el-menu-item>
                <el-menu-item v-if="!accountStore.isSubAccount" index="/me/invitationRecord"> 受邀记录 </el-menu-item>
                <el-menu-item v-if="accountStore.isRootAccount" index="/me/SMSManagement"> 短信管理 </el-menu-item>
                <el-menu-item v-if="!accountStore.isSubAccount" index="/me/invoice"> 发票抬头 </el-menu-item>
                <el-menu-item v-if="!accountStore.isSubAccount" index="/me/address"> 我的地址 </el-menu-item>
                <el-menu-item index="/me/bankAccount"> 银行账户 </el-menu-item>
            </el-sub-menu>
            <el-menu-item v-if="isShow(['/me/']) && isChainCloud" index="/me/member">
                <template #title>
                    <el-icon>
                        <i class="iconfont icon-a-member1" />
                    </el-icon>
                    <span>我的会员</span>
                </template>
            </el-menu-item>
            <el-sub-menu v-if="isShow(consoleAboutPath)" index="order">
                <template #title>
                    <el-icon>
                        <i class="iconfont icon-files" />
                    </el-icon>
                    <span>交易中心</span>
                </template>
                <el-menu-item v-for="(item, i) in orderMenus" :key="i" :index="item.path" @click="switchTab(item)">
                    {{ item.name }}
                </el-menu-item>
            </el-sub-menu>
            <el-sub-menu v-if="isShow(consoleAboutPath)" index="logistics">
                <template #title>
                    <el-icon>
                        <i class="iconfont icon-logistics" />
                    </el-icon>
                    <span>物流中心</span>
                </template>
                <el-menu-item v-for="(item, i) in logisticsMenus" :key="i" :index="item.path" @click="switchTab(item)">
                    {{ item.name }}
                </el-menu-item>
            </el-sub-menu>
            <el-sub-menu v-if="showEnterpriseAnnouncementsMenu()" index="messageRelease">
                <template #title>
                    <el-icon>
                        <i class="iconfont icon-a-xunxi1" />
                    </el-icon>
                    <span>讯息发布</span>
                </template>
                <el-menu-item
                    v-for="(item, i) in enterpriseAnnouncementsMenus"
                    :key="i"
                    :index="item.path"
                    @click="switchTab(item)"
                >
                    {{ item.name }}
                </el-menu-item>
            </el-sub-menu>
            <el-sub-menu v-if="isShow(consoleAboutPath)" index="follow">
                <template #title>
                    <el-icon>
                        <i class="iconfont icon-aixin-1" />
                    </el-icon>
                    <span>关注中心</span>
                </template>
                <el-menu-item v-for="(item, i) in followMenus" :key="i" :index="item.path" @click="switchTab(item)">
                    {{ item.name }}
                </el-menu-item>
            </el-sub-menu>
            <el-menu-item v-if="isShow(consoleAboutPath)" index="/registration/myRegistration">
                <template #title>
                    <el-icon>
                        <i class="iconfont icon-registration" />
                    </el-icon>
                    <span>我的报名</span>
                </template>
            </el-menu-item>
        </el-menu>
        <div class="navigation" @click="changeCollapse">
            <i class="iconfont icon-a-putaway" />
            <span class="navigation-text">收起导航</span>
        </div>
    </div>
    <span v-if="isCollapse" class="collapse" @click="changeCollapse"><i class="iconfont icon-a-putaway1" /></span>
</template>

<style scoped lang="scss">
.menu {
    height: 100%;
    flex-shrink: 0;
    border-radius: 8px;
    background: #fff;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    .el-menu {
        border-radius: 8px;
    }
    .el-menu-item.is-active {
        color: #1966ff;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 14px;
    }
    .el-menu-vertical {
        overflow: hidden;
        height: 100%;
        box-sizing: border-box;
        border-right: none !important;
    }
    .el-menu-vertical:not(.el-menu--collapse) {
        width: 143px;
        height: 100%;
        box-sizing: border-box;
        border-right: none !important;
    }
    .navigation {
        cursor: pointer;
        box-sizing: border-box;
        display: flex;
        width: 100%;
        height: 44px;
        padding: 6px 24px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
        border-top: 1px solid #d9d9d9;
        margin: 0;
        z-index: 100;
        &:hover {
            background-color: var(--el-menu-hover-bg-color);
        }
    }
    .navigation-text {
        color: #000000e0;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 14px;
        text-transform: capitalize;
    }
}
.collapse {
    cursor: pointer;
    position: fixed;
    left: 16px;
    bottom: 34px;
}
</style>
